/**
 * Application entrypoint. Mounts the React app into the DOM.
 */
import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import App from "@/App";
import ErrorBoundary from "./components/ErrorBoundary";
import logger from "./lib/logger";
import { prefetchCustomerPricing } from "./lib/bootstrap-data";

if (typeof window !== "undefined") {
  prefetchCustomerPricing();
}

// Suppress noisy ResizeObserver loop errors that originate from browser
// ResizeObserver flush cycles in some layouts. These errors are benign
// in most cases; gracefully ignore them to avoid uncaught runtime errors
// shown in the console during development.
if (typeof window !== "undefined") {
  const _origConsoleError = console.error.bind(console);
  console.error = (...args) => {
    try {
      const first = args[0];
      if (typeof first === "string" && first.includes("ResizeObserver loop completed")) return;
      if (first && first.message && typeof first.message === "string" && first.message.includes("ResizeObserver loop completed")) return;
    } catch (e) {
      // ignore
    }
    _origConsoleError(...args);
  };

  window.addEventListener("error", (event) => {
    try {
      const msg = event?.message || "";
      if (typeof msg === "string" && msg.includes("ResizeObserver loop completed")) {
        event.stopImmediatePropagation();
        event.preventDefault();
        return;
      }
      logger.error("window.error", {
        message: msg,
        filename: event?.filename,
        lineno: event?.lineno,
        colno: event?.colno,
        stack: event?.error?.stack,
      });
    } catch (e) {
      // defensive: don't break app if event handling throws
    }
  });

  window.addEventListener("unhandledrejection", (ev) => {
    try {
      const reason = ev?.reason?.message || ev?.reason || "";
      if (typeof reason === "string" && reason.includes("ResizeObserver loop completed")) {
        ev.preventDefault();
        return;
      }
      logger.error("window.unhandledrejection", {
        reason: typeof reason === "string" ? reason : reason?.message || String(reason),
        stack: ev?.reason?.stack,
      });
    } catch (e) {
      // ignore
    }
  });
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  </React.StrictMode>,
);
